import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  TextField,
  Button,
  Paper,
  Alert,
  IconButton,
  Tooltip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import TimerIcon from '@mui/icons-material/Timer';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate } from 'react-router-dom';

const InteractiveCipherText = ({ encodedMessage, onDecryptionChange, autoFillSameLetters = true, title, onTextBoxesChange }) => {
  const [decryptionMap, setDecryptionMap] = useState({});
  const [textBoxes, setTextBoxes] = useState([]);
  const [focusedIndex, setFocusedIndex] = useState(null);
  const [errorIndex, setErrorIndex] = useState(null);
  const [usedLetters, setUsedLetters] = useState({});

  // Check if this is a Vigenère cipher or Hill cipher
  const isVigenere = title === "Vigenère Cipher Challenge";
  const isHill = title === "Hill Cipher Challenge";
  
  // Debug log to verify title is being passed correctly
  useEffect(() => {
    console.log('Current cipher type:', title, 'Is Vigenère:', isVigenere, 'Is Hill:', isHill);
  }, [title, isVigenere, isHill]);

  useEffect(() => {
    // Initialize text boxes for each letter in the encoded message
    const boxes = encodedMessage.split('').map((char, index) => {
      if (char.match(/[a-z]/i)) {
        if (isVigenere || isHill) {
          // For Vigenère and Hill ciphers, use the index as the key
          return {
            id: index,
            char: char,
            value: decryptionMap[index] || ''
          };
        } else {
          // For other ciphers, use the character as the key
          const upperChar = char.toUpperCase();
          return {
            id: index,
            char: char,
            value: decryptionMap[upperChar] || ''
          };
        }
      }
      return {
        id: index,
        char: char,
        value: char,
        isSpecial: true
      };
    });
    setTextBoxes(boxes);
    if (onTextBoxesChange) {
      onTextBoxesChange(boxes);
    }
  }, [encodedMessage, decryptionMap, isVigenere, isHill, onTextBoxesChange]);

  const findNextInputBox = (currentIndex) => {
    // Find the next non-special character index that's empty
    for (let i = currentIndex + 1; i < textBoxes.length; i++) {
      const box = textBoxes[i];
      if (!box.isSpecial && !box.value) {
        return i;
      }
    }
    return -1;
  };

  const findPreviousInputBox = (currentIndex) => {
    // Find the previous non-special character index
    for (let i = currentIndex - 1; i >= 0; i--) {
      const box = textBoxes[i];
      if (!box.isSpecial) {
        return i;
      }
    }
    return -1;
  };

  const focusInputBox = (index) => {
    const inputBox = document.querySelector(`input[data-index="${index}"]`);
    if (inputBox) {
      inputBox.focus();
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === 'Backspace') {
      const currentBox = textBoxes[index];
      
      // If current box has a value, clear it first
      if (currentBox.value) {
        handleTextBoxChange(currentBox.char, '', index);
        return;
      }
      
      // If current box is empty, move to and clear previous box
      const prevIndex = findPreviousInputBox(index);
      if (prevIndex !== -1) {
        const prevBox = textBoxes[prevIndex];
        handleTextBoxChange(prevBox.char, '', prevIndex);
        // Focus the previous box
        const prevInput = document.querySelector(`input[data-index="${prevIndex}"]`);
        if (prevInput) {
          prevInput.focus();
        }
      }
    }
  };

  const handleTextBoxChange = (char, value, index) => {
    // Convert input to uppercase and ensure it's a single letter
    let upperValue = value ? value.toUpperCase().slice(0, 1) : '';
    
    // For Hill cipher, only allow letters
    if (isHill && upperValue && !/^[A-Z]$/.test(upperValue)) {
      upperValue = '';
    }
    
    // Update the text boxes with the new value
    const newTextBoxes = textBoxes.map(box => {
      if (box.id === index) {
        return { ...box, value: upperValue };
      }
      // Skip auto-fill for Vigenère and Hill ciphers
      if (!isVigenere && !isHill && autoFillSameLetters && !box.isSpecial && box.char.toLowerCase() === char.toLowerCase()) {
        return { ...box, value: upperValue };
      }
      return box;
    });
    
    setTextBoxes(newTextBoxes);
    if (onTextBoxesChange) {
      onTextBoxesChange(newTextBoxes);
    }
    
    // Update used letters tracking
    const newUsedLetters = {};
    
    // Collect all current letter assignments
    newTextBoxes.forEach((box) => {
      if (box.value && !box.isSpecial) {
        if (!newUsedLetters[box.value]) {
          newUsedLetters[box.value] = new Set();
        }
        newUsedLetters[box.value].add(box.char.toUpperCase());
      }
    });
    
    setUsedLetters(newUsedLetters);
    
    // For Vigenère and Hill ciphers, we'll use a different approach to store mappings
    if (isVigenere || isHill) {
      // Store the mapping only for this specific position
      const newDecryptionMap = { ...decryptionMap };
      if (upperValue) {
        newDecryptionMap[index] = upperValue;
      } else {
        delete newDecryptionMap[index];
      }
      setDecryptionMap(newDecryptionMap);
    } else {
      // Original behavior for other ciphers
      const newDecryptionMap = { ...decryptionMap };
      if (upperValue) {
        const upperChar = char.toUpperCase();
        const lowerChar = char.toLowerCase();
        newDecryptionMap[upperChar] = upperValue;
        newDecryptionMap[lowerChar] = upperValue;
      } else {
        const upperChar = char.toUpperCase();
        const lowerChar = char.toLowerCase();
        delete newDecryptionMap[upperChar];
        delete newDecryptionMap[lowerChar];
      }
      setDecryptionMap(newDecryptionMap);
    }
    
    // Update the decrypted text
    const decryptedText = encodedMessage
      .split('')
      .map((c, i) => {
        if (isVigenere || isHill) {
          // For Vigenère and Hill, check position-specific mapping
          return decryptionMap[i] || c;
        } else {
          // Original behavior for other ciphers
          const upperC = c.toUpperCase();
          const lowerC = c.toLowerCase();
          return decryptionMap[upperC] || decryptionMap[lowerC] || c;
        }
      })
      .join('');
    
    onDecryptionChange(decryptedText);

    // Auto-focus next box for all ciphers when a letter is typed
    if (value) {
      // Find the next empty box immediately using the new state
      let nextIndex = index;
      while (nextIndex < newTextBoxes.length) {
        nextIndex++;
        const box = newTextBoxes[nextIndex];
        if (box && !box.isSpecial && !box.value) {
          // Focus the next box immediately
          const nextInput = document.querySelector(`input[data-index="${nextIndex}"]`);
          if (nextInput) {
            nextInput.focus();
          }
          break;
        }
      }
    }
  };

  const handleFocus = (index) => {
    setFocusedIndex(index);
    // Clear error state when focus changes
    setErrorIndex(null);
  };

  // Group characters into words for better wrapping
  const words = textBoxes.reduce((acc, box, index) => {
    if (box.isSpecial && box.char === ' ') {
      acc.push([]);
    } else {
      if (acc.length === 0) acc.push([]);
      acc[acc.length - 1].push(box);
    }
    return acc;
  }, [[]]);

  return (
    <Box sx={{ 
      display: 'flex', 
      flexWrap: 'wrap', 
      gap: '4px',
      justifyContent: 'flex-start',
      fontFamily: 'monospace',
      fontSize: '1.2rem',
      p: 2,
      bgcolor: 'grey.100',
      borderRadius: 1,
      maxWidth: '100%',
      overflow: 'hidden',
      wordBreak: 'break-word'
    }}>
      {words.map((word, wordIndex) => (
        <Box 
          key={wordIndex} 
          sx={{ 
            display: 'flex', 
            flexDirection: 'row',
            flexWrap: 'wrap',
            mr: 2,
            mb: 1,
            maxWidth: '100%'
          }}
        >
          {word.map((box) => (
            <Box 
              key={box.id} 
              sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center',
                mx: 0.5,
                minWidth: '24px'
              }}
            >
              <TextField
                value={box.value}
                onChange={(e) => handleTextBoxChange(box.char, e.target.value, box.id)}
                onKeyDown={(e) => handleKeyDown(e, box.id)}
                onFocus={() => handleFocus(box.id)}
                error={box.id === errorIndex}
                disabled={box.isSpecial}
                inputProps={{
                  maxLength: 1,
                  style: {
                    width: '24px',
                    height: '24px',
                    padding: '4px',
                    textAlign: 'center',
                    fontSize: '1rem',
                    color: (!isVigenere && !isHill && box.value && (usedLetters[box.value]?.size > 1)) ? 'red' : 'inherit',
                  },
                  'data-index': box.id
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    height: '32px',
                  },
                  '& .MuiOutlinedInput-root.Mui-disabled': {
                    backgroundColor: 'transparent',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'transparent'
                    }
                  }
                }}
                autoFocus={box.id === focusedIndex}
              />
              <Typography sx={{ mt: 0.5, minWidth: '24px', textAlign: 'center' }}>
                {box.isSpecial ? '' : box.char.toUpperCase()}
              </Typography>
            </Box>
          ))}
        </Box>
      ))}
    </Box>
  );
};

function LetterFrequencyTable({ encodedMessage }) {
  // Count letter frequencies in the encoded message
  const letterCounts = {};
  'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('').forEach(letter => {
    letterCounts[letter] = 0;
  });
  encodedMessage.toUpperCase().split('').forEach(char => {
    if (/[A-Z]/.test(char)) {
      letterCounts[char]++;
    }
  });
  const letters = Object.keys(letterCounts);
  const counts = Object.values(letterCounts);

  return (
    <Box sx={{ mt: 4, mb: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1, maxWidth: 900, mx: 'auto' }}>
      <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', fontFamily: 'monospace', fontSize: '0.95rem', textAlign: 'center' }}>
        <thead>
          <tr>
            <th style={{ border: '2px solid black', padding: '2px', minWidth: '28px' }}>Ltrs</th>
            {letters.map(letter => (
              <th key={letter} style={{ border: '2px solid black', padding: '2px', minWidth: '22px' }}>{letter}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          <tr>
            <td style={{ border: '2px solid black', padding: '2px', fontWeight: 'bold' }}>Frequency</td>
            {counts.map((count, idx) => (
              <td key={letters[idx]} style={{ border: '2px solid black', padding: '2px' }}>{count}</td>
            ))}
          </tr>
        </tbody>
      </Box>
    </Box>
  );
}

const CipherChallenge = ({
  title,
  description,
  encodedMessage,
  correctAnswer,
  originalText,
  hint,
  difficulty,
  onComplete,
  userShift,
  setUserShift,
  correctShift,
  autoFillSameLetters = true,
  allowDuplicateLetters = false,
  challenge,
  onDecryptionChange
}) => {
  const navigate = useNavigate();
  const [userAnswer, setUserAnswer] = useState('');
  const [feedback, setFeedback] = useState(null);
  const [showHint, setShowHint] = useState(false);
  const [attempts, setAttempts] = useState(0);
  const [showAnswer, setShowAnswer] = useState(false);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [isTimerRunning, setIsTimerRunning] = useState(true);
  const [solved, setSolved] = useState(false);
  const [textBoxes, setTextBoxes] = useState([]);
  const [showGiveUpWarning, setShowGiveUpWarning] = useState(false);

  useEffect(() => {
    let timer;
    if (isTimerRunning) {
      timer = setInterval(() => {
        setElapsedTime(prev => prev + 1);
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [isTimerRunning]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleCheck = () => {
    setAttempts(prev => prev + 1);

    // Get user's answer from text boxes
    const getUserAnswer = () => {
      if (!textBoxes || textBoxes.length === 0) return '';
      
      return textBoxes
        .map(box => {
          if (box.isSpecial) return box.char;
          return box.value || box.char;
        })
        .join('');
    };

    try {
      const userAnswer = getUserAnswer();

      // Normalize both answers by removing spaces and converting to uppercase
      const normalizedUserAnswer = userAnswer.replace(/[^A-Za-z]/g, '').toUpperCase();
      const normalizedCorrectAnswer = correctAnswer.replace(/[^A-Za-z]/g, '').toUpperCase();

      // Log both answers for debugging
      console.log('Debug - Comparing:', {
        title,
        userAnswer,
        correctAnswer,
        normalizedUserAnswer,
        normalizedCorrectAnswer,
        'Are equal?': normalizedUserAnswer === normalizedCorrectAnswer
      });

      // For Caesar Cipher, we need to check both text and shift
      if (title === "Caesar Cipher Challenge") {
        const userShiftNum = parseInt(userShift, 10);
        
        if (normalizedUserAnswer === normalizedCorrectAnswer && userShiftNum === correctShift) {
          handleSuccess();
        } else if (normalizedUserAnswer === normalizedCorrectAnswer) {
          handleFailure('The text is correct, but the shift value is wrong!');
        } else if (userShiftNum === correctShift) {
          handleFailure('The shift value is correct, but the text is wrong!');
        } else {
          handleFailure('Both the text and shift value are incorrect. Keep trying!');
        }
        return;
      }

      // For all other ciphers, do a direct comparison of normalized text
      if (normalizedUserAnswer === normalizedCorrectAnswer) {
        handleSuccess();
      } else {
        // Check if any boxes are empty
        const hasEmptyBoxes = textBoxes.some(box => !box.isSpecial && !box.value);
        if (hasEmptyBoxes) {
          handleFailure('Make sure to fill in all the boxes!');
        } else {
          handleFailure('Not quite right. Keep trying!');
        }
      }
    } catch (error) {
      console.error('Error in handleCheck:', error);
      handleFailure('Something went wrong. Please try again.');
    }
  };

  const handleSuccess = () => {
    setIsTimerRunning(false);
    setFeedback({
      type: 'success',
      message: `Congratulations! You solved it in ${formatTime(elapsedTime)}! Click "Next Challenge" to continue.`,
    });
    setSolved(true);
    if (onComplete) {
      onComplete({
        time: elapsedTime,
        attempts,
        success: true,
      });
    }
  };

  const handleFailure = (message) => {
    setFeedback({
      type: 'error',
      message: message,
    });
    if (onComplete) {
      onComplete({
        time: elapsedTime,
        attempts,
        success: false,
      });
    }
  };

  const handleDecryptionChange = (decryptedText) => {
    // No longer using userAnswer state
    if (onDecryptionChange) {
      onDecryptionChange(decryptedText);
    }
  };

  const isAnswerComplete = () => {
    if (!textBoxes || textBoxes.length === 0) return false;
    
    // For Caesar Cipher, check both the shift and all text boxes
    if (title === "Caesar Cipher Challenge") {
      const hasValidShift = userShift && !isNaN(parseInt(userShift, 10));
      const allBoxesFilled = textBoxes
        .filter(box => !box.isSpecial) // Only check non-special characters
        .every(box => box.value && box.value.trim() !== '');
      return hasValidShift && allBoxesFilled;
    }

    // For Hill Cipher
    if (title === "Hill Cipher Challenge") {
      return textBoxes
        .filter(box => !box.isSpecial)
        .every(box => box.value && box.value.trim() !== '');
    }

    // For all other ciphers
    return textBoxes
      .filter(box => !box.isSpecial) // Only check non-special characters
      .every(box => box.value && box.value.trim() !== ''); // Check if each box has a value
  };

  const handleBack = () => {
    navigate(-1);
  };

  const handleSubmit = () => {
    setAttempts(prev => prev + 1);
    
    // Normalize both strings by converting to lowercase and removing punctuation/spaces
    const normalizedUserAnswer = userAnswer.toLowerCase().replace(/[^a-z]/g, '');
    const normalizedCorrectAnswer = correctAnswer.toLowerCase().replace(/[^a-z]/g, '');

    if (normalizedUserAnswer === normalizedCorrectAnswer) {
      handleSuccess();
    } else {
      handleFailure('Incorrect answer. Try again!');
    }
  };

  const handleGiveUp = () => {
    if (attempts < 3) {
      setShowGiveUpWarning(true);
    } else {
      setShowAnswer(true);
      onComplete({ success: true }); // Enable the New Challenge button
    }
  };

  const remainingAttempts = 3 - attempts;

  return (
    <Container maxWidth="md">
      <Typography
        variant="h2"
        component="h1"
        gutterBottom
        align="center"
        sx={{
          fontWeight: 700,
          fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
          color: '#000000',
          mb: 4,
          position: 'relative',
          '&::after': {
            content: '""',
            position: 'absolute',
            bottom: '-10px',
            left: '50%',
            transform: 'translateX(-50%)',
            width: '100px',
            height: '3px',
            background: 'linear-gradient(90deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.3) 50%, rgba(0,0,0,0) 100%)',
            borderRadius: '2px',
          }
        }}
      >
        {title}
      </Typography>
      <Box sx={{ my: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <IconButton onClick={handleBack} sx={{ mr: 2 }}>
            <ArrowBackIcon fontSize="large" />
          </IconButton>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 2 }}>
            <TimerIcon />
            <Typography variant="body2" color="text.secondary">
              Difficulty: {difficulty}
            </Typography>
          </Box>
        </Box>

        <Paper elevation={3} sx={{ p: 3, mb: 3, backgroundColor: '#ffffff' }}>
          <Typography 
            variant="h6" 
            gutterBottom
            sx={{
              position: 'relative',
              pb: 2,
              mb: 3,
              '&::after': {
                content: '""',
                position: 'absolute',
                bottom: 0,
                left: 0,
                width: '60px',
                height: '2px',
                background: 'linear-gradient(90deg, rgba(0,0,0,0.3) 0%, rgba(0,0,0,0) 100%)',
                borderRadius: '2px',
              }
            }}
          >
            Encoded Message:
          </Typography>
          <InteractiveCipherText 
            encodedMessage={encodedMessage}
            onDecryptionChange={handleDecryptionChange}
            autoFillSameLetters={autoFillSameLetters}
            title={title}
            onTextBoxesChange={setTextBoxes}
          />
        </Paper>

        <LetterFrequencyTable encodedMessage={encodedMessage} />

        <Box sx={{ mb: 3 }}>
          {correctShift !== undefined && (
            <TextField
              label="Shift Amount"
              type="number"
              value={userShift}
              onChange={e => setUserShift(e.target.value.replace(/[^0-9]/g, ''))}
              sx={{ mb: 2, width: 180 }}
              inputProps={{ min: 1, max: 25 }}
            />
          )}
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 3 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleCheck}
              disabled={!isAnswerComplete() || solved}
            >
              Check Answer
            </Button>
            {solved && (
              <Button
                variant="contained"
                color="success"
                onClick={() => {
                  if (onComplete) {
                    onComplete({
                      time: elapsedTime,
                      attempts,
                      success: true,
                      moveToNext: true
                    });
                  }
                }}
              >
                Next Challenge
              </Button>
            )}
            <Button
              variant="outlined"
              color="secondary"
              onClick={handleGiveUp}
              disabled={solved}
            >
              Give Up
            </Button>
          </Box>
        </Box>

        {showHint && (
          <Alert severity="info" sx={{ mb: 3 }}>
            {hint}
          </Alert>
        )}

        {feedback && (
          <Alert severity={feedback.type} sx={{ mb: 3 }}>
            {feedback.message}
          </Alert>
        )}

        {showAnswer && (
          <Alert severity="info" sx={{ mb: 3 }}>
            {title === "Hill Cipher Challenge" ? (
              <>
                <Typography variant="body1" gutterBottom>
                  <strong>Complete solution:</strong>
                </Typography>
                <Typography variant="body1" gutterBottom>
                  Original text: {originalText}
                </Typography>
                <Typography variant="body1" gutterBottom>
                  Letters only (including padding): {correctAnswer.toUpperCase()}
                  {challenge?.paddingLetters && (
                    <span style={{ color: 'blue' }}>{challenge.paddingLetters.toUpperCase()}</span>
                  )}
                </Typography>
                {challenge?.paddingLetters && (
                  <Typography variant="body2" color="text.secondary">
                    Note: Blue letters are padding that were added to complete the last block. These letters must be decrypted correctly.
                  </Typography>
                )}
              </>
            ) : (
              <>The correct answer is: {originalText || correctAnswer}</>
            )}
          </Alert>
        )}

        {/* Give Up Warning Dialog */}
        <Dialog
          open={showGiveUpWarning}
          onClose={() => setShowGiveUpWarning(false)}
        >
          <DialogTitle>Not Enough Attempts</DialogTitle>
          <DialogContent>
            <Typography>
              You must attempt the cipher {remainingAttempts} more time{remainingAttempts !== 1 ? 's' : ''} before giving up.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowGiveUpWarning(false)}>OK</Button>
          </DialogActions>
        </Dialog>

        {/* Answer Dialog */}
        <Dialog
          open={showAnswer}
          onClose={() => setShowAnswer(false)}
        >
          <DialogTitle>Answer</DialogTitle>
          <DialogContent>
            <Typography>
              {correctAnswer}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowAnswer(false)}>Close</Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Container>
  );
};

export default CipherChallenge; 